/**
 * Pure logic for staff Responses across versions: which columns show, what each cell holds, the
 * Changed / New / Reworded badges, the summary line and the CSV. The page only renders this.
 */
import type { AnsweredBy, AnswerValue, CompareAnswerSet, CompareNote, CompareQuestion, CompareQuestionVersion, ResponsesCompare } from "@/api/types";
import { isBlank } from "@/features/questionnaire/answerText";

type C = ResponsesCompare;

/** RFC 4180: quote fields containing comma, quote, CR or LF; double embedded quotes. */
export function csvField(s: string): string {
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
type Value = AnswerValue | null;

export type Cell = { kind: "notAsked" } | { kind: "answer"; value: Value; entry: CompareQuestionVersion; by: AnsweredBy | null };

/** Versions on by default, newest first: the latest two, or the latest only on phones. */
export function defaultVisible(c: C, isPhone: boolean): number[] {
  return c.versions.slice(0, isPhone ? 1 : 2).map((v) => v.version);
}

/** Turn a version on or off; the last one on stays on. */
export function toggleVersion(visible: number[], version: number): number[] {
  if (visible.includes(version)) return visible.length > 1 ? visible.filter((v) => v !== version) : visible;
  return [...visible, version].sort((a, b) => b - a);
}

export function latestVersion(c: C): number | null {
  return c.versions[0]?.version ?? null;
}

/** The organisation's shared answers on `version`; undefined means not started. */
export function answerSet(c: C, version: number): CompareAnswerSet | undefined {
  return c.answers.find((a) => a.version === version);
}

/** How the question looked in `version` if it was asked then (present and not deprecated). */
export function askedIn(q: CompareQuestion, version: number): CompareQuestionVersion | undefined {
  const entry = q.versions.find((v) => v.version === version);
  return entry && !entry.deprecated ? entry : undefined;
}

export function cellFor(c: C, q: CompareQuestion, version: number): Cell {
  const entry = askedIn(q, version);
  if (!entry) return { kind: "notAsked" };
  const set = answerSet(c, version);
  const value = set?.answers[q.id] ?? null;
  return { kind: "answer", value, entry, by: isBlank(value) ? null : (set?.answered_by[q.id] ?? null) };
}

const canonical = (v: Value) => JSON.stringify(Array.isArray(v) ? [...v].sort() : v);

/** The answer in `version` differs from the next older visible version's. */
export function changed(c: C, q: CompareQuestion, visible: number[], version: number): boolean {
  const older = visible.filter((v) => v < version).sort((a, b) => b - a)[0];
  if (older === undefined) return false;
  const now = cellFor(c, q, version);
  const before = cellFor(c, q, older);
  if (now.kind !== "answer" || before.kind !== "answer" || isBlank(now.value) || isBlank(before.value)) return false;
  return canonical(now.value) !== canonical(before.value);
}

/** First asked in the latest version (and there is an earlier one to be new against). */
export function isNew(c: C, q: CompareQuestion): boolean {
  const latest = latestVersion(c);
  return q.in_latest && latest !== null && latest > 1 && q.first_version === latest && c.versions.length > 1;
}

/** The newest version whose diff changed this question's wording, type or options. */
export function rewordedIn(c: C, q: CompareQuestion): { version: number; previous: CompareQuestionVersion } | null {
  if (!q.in_latest) return null;
  for (const v of c.versions) {
    if (v.version <= q.first_version || !v.diff.changed.includes(q.id)) continue;
    const previous = q.versions.find((e) => e.version < v.version);
    if (previous) return { version: v.version, previous };
  }
  return null;
}

/** Row groups: the latest version's sections in order, then `id: null` for questions no longer asked. */
export function groupRows(c: C): { id: string | null; title: string | null; questions: CompareQuestion[] }[] {
  const groups: { id: string | null; title: string | null; questions: CompareQuestion[] }[] = [];
  const gone: CompareQuestion[] = [];
  for (const q of c.questions) {
    if (!q.in_latest) {
      gone.push(q);
      continue;
    }
    const entry = q.versions[0]!;
    const last = groups.at(-1);
    if (last && last.id === entry.section_id) last.questions.push(q);
    else groups.push({ id: entry.section_id, title: entry.section_title, questions: [q] });
  }
  if (gone.length) groups.push({ id: null, title: null, questions: gone });
  return groups;
}

export type Summary = {
  version: number;
  status: "submitted" | "draft" | "none";
  submittedBy?: AnsweredBy;
  answered: number;
  total: number;
  added: number;
  reworded: number;
  since: number | null;
};

/** The latest version: its status, who submitted, and how many answerable questions have an answer. */
export function summary(c: C): Summary | null {
  const [latest, previous] = c.versions;
  if (!latest) return null;
  const inLatest = new Set(c.questions.filter((q) => q.in_latest).map((q) => q.id));
  const set = answerSet(c, latest.version);
  const answerable = c.questions.filter((q) => {
    const e = askedIn(q, latest.version);
    return e && e.type !== "info";
  });
  const answered = answerable.filter((q) => !isBlank(set?.answers[q.id])).length;
  return {
    version: latest.version,
    status: set?.status ?? "none",
    ...(set?.submitted_by ? { submittedBy: set.submitted_by } : {}),
    answered,
    total: answerable.length,
    added: previous ? latest.diff.added.filter((id) => inLatest.has(id)).length : 0,
    reworded: previous ? latest.diff.changed.filter((id) => inLatest.has(id)).length : 0,
    since: previous?.version ?? null,
  };
}

/** Notes on a question: the latest version's (editable, one per author) and earlier read-only ones. */
export function notesFor(c: C, questionId: string): { latest: CompareNote[]; earlier: CompareNote[] } {
  const latest = latestVersion(c);
  const mine = c.notes.filter((n) => n.question_id === questionId);
  return {
    latest: mine.filter((n) => n.version === latest),
    earlier: mine.filter((n) => n.version !== latest).sort((a, b) => b.version - a.version),
  };
}

/** What's on screen as CSV: one row per question, an answer and an answered-by column per visible version. */
export function compareCsv(
  c: C,
  visible: number[],
  format: (entry: CompareQuestionVersion, value: Value) => string,
  words: { notAsked: string; noLongerAsked: string },
): string {
  const header = ["question id", "section", "question"];
  for (const v of visible) header.push(`v${v} answer`, `v${v} answered_by`);
  header.push("note");
  const rows: string[][] = [header];
  for (const group of groupRows(c)) {
    for (const q of group.questions) {
      const row = [q.id, group.title ?? words.noLongerAsked, q.versions[0]!.title];
      for (const v of visible) {
        const cell = cellFor(c, q, v);
        row.push(cell.kind === "notAsked" ? words.notAsked : format(cell.entry, cell.value), cell.kind === "answer" ? (cell.by?.name ?? "") : "");
      }
      row.push(
        notesFor(c, q.id)
          .latest.map((n) => `${n.author_name}: ${n.note}`)
          .join(" | "),
      );
      rows.push(row);
    }
  }
  return rows.map((r) => r.map(csvField).join(",")).join("\r\n") + "\r\n";
}

export function csvFilename(slug: string, visible: number[]): string {
  return `responses-${slug}-${visible.map((v) => `v${v}`).join("+")}.csv`;
}
